import { LocalizedInputWithLabel } from '@components/common/LocalizedInputWithLabel';
import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import { useVoiceStudio } from '@hooks/useVoiceStudio';
import { type Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextsGroup from './TextsGroup';
import VoiceTextsFields from './VoiceTextsFields';

interface WebclientTextsFieldsProps {
  control: Control<any>;
  // Suffixed to the input ids when several configs are open on one page.
  configId?: string;
  // The Edit Appearance panel uses smaller inputs than the config modal.
  isCompact?: boolean;
}

// Every text the visitor sees, grouped by the part of the widget it fills in.
// Shared by the Edit Appearance panel and the webclient config modal.
const WebclientTextsFields: React.FC<WebclientTextsFieldsProps> = ({
  control,
  configId,
  isCompact = false,
}) => {
  const { t } = useTranslation();
  const { isVoiceOnlyStudio, isVoiceConfigurable } = useVoiceStudio();
  const isVoiceToggleOn = useWatch({ control, name: 'options.voice.enabled' });

  // A Voice A.I. Studio is voice-only by definition; a chat studio only shows
  // the call when its voice tab is switched on.
  const isVoiceEnabled =
    isVoiceConfigurable && (isVoiceOnlyStudio || Boolean(isVoiceToggleOn));

  const buildId = (name: string) => {
    if (!configId) {
      return name;
    }
    return `${name}-${configId}`;
  };

  const inputClassName = isCompact ? 'text-xs leading-5' : '';
  const labelClassName = isCompact ? 'text-xs font-medium' : '';
  const checkboxLabelClassName = isCompact ? 'text-xs' : '';

  const sharedInputProps = { control, inputClassName, labelClassName };

  return (
    <div className='flex flex-col'>
      <TextsGroup
        title={t('settings.webclient.config.form.texts-group.header')}
        icon='title'
        isFirst>
        <LocalizedInputWithLabel
          {...sharedInputProps}
          field='options.title'
          id={buildId('title')}
          label={t('settings.webclient.config.form.config.title')}
          placeholder={t('settings.webclient.config.form.config.title')}
          required={t('general.validation.required')}
        />
        <LocalizedInputWithLabel
          {...sharedInputProps}
          field='options.subtitle'
          id={buildId('subtitle')}
          label={t('settings.webclient.config.form.config.subtitle')}
          placeholder={t('settings.webclient.config.form.config.subtitle')}
          required={t('general.validation.required')}
        />
      </TextsGroup>

      {/* The greeting popup shows the welcome message in every studio */}
      <TextsGroup
        title={t('settings.webclient.config.form.texts-group.greeting')}
        icon='waving_hand'>
        <LocalizedInputWithLabel
          {...sharedInputProps}
          field='options.welcomeMessage'
          id={buildId('welcomeMessage')}
          label={t('settings.webclient.config.form.config.welcome-message')}
          placeholder={t(
            'settings.webclient.config.form.config.welcome-message',
          )}
          required={t('general.validation.required')}
        />
        {/* A voice-only widget has no chat transcript to open with it */}
        {!isVoiceOnlyStudio && (
          <CheckboxInputT
            label={t(
              'settings.webclient.config.form.config.show-welcome-message',
            )}
            control={control}
            field='options.showWelcomeAsFirstMessage'
            id={buildId('showWelcomeAsFirstMessage')}
            labelClassName={checkboxLabelClassName}
          />
        )}
        <LocalizedInputWithLabel
          {...sharedInputProps}
          field='options.greeting.buttonText'
          id={buildId('greetingButtonText')}
          label={t('settings.webclient.config.form.config.greeting-button')}
          placeholder={t(
            'settings.webclient.config.form.config.greeting-button',
          )}
        />
      </TextsGroup>

      {/* A voice-only widget renders no chat input, so none of this copy
      reaches the visitor */}
      {!isVoiceOnlyStudio && (
        <TextsGroup
          title={t('settings.webclient.config.form.texts-group.chat')}
          icon='chat'>
          <LocalizedInputWithLabel
            {...sharedInputProps}
            field='options.messagePlaceholder'
            id={buildId('messagePlaceholder')}
            label={t('settings.webclient.config.form.config.placeholder')}
            placeholder={t('settings.webclient.config.form.config.placeholder')}
          />
          <LocalizedInputWithLabel
            {...sharedInputProps}
            field='options.waitingForTheBotResponseMessage'
            id={buildId('waitingForTheBotResponseMessage')}
            label={t(
              'settings.webclient.config.form.config.waiting-for-the-bot-response-message',
            )}
            placeholder={t(
              'settings.webclient.config.form.config.waiting-for-the-bot-response-message',
            )}
            required={t('general.validation.required')}
          />
          <CheckboxInputT
            label={t(
              'settings.webclient.config.form.config.enable-waiting-for-the-bot-response-blocker',
            )}
            control={control}
            field='options.enableWaitingForBotResponseBlocker'
            id={buildId('enableWaitingForBotResponseBlocker')}
            labelClassName={checkboxLabelClassName}
          />
          <LocalizedInputWithLabel
            {...sharedInputProps}
            field='options.notificationAIGeneratedAnswer'
            id={buildId('notificationAIGeneratedAnswer')}
            label={t(
              'settings.webclient.config.form.config.ai-generated-answer',
            )}
            placeholder={t(
              'settings.webclient.config.form.config.ai-generated-answer',
            )}
            required={t('general.validation.required')}
          />
        </TextsGroup>
      )}

      {/* The tab bar only renders in a chat studio with the voice tab on */}
      {isVoiceEnabled && !isVoiceOnlyStudio && (
        <VoiceTextsFields
          part='tabs'
          control={control}
          idPrefix={configId || 'webclientModal'}
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          checkboxLabelClassName={checkboxLabelClassName}
        />
      )}

      {isVoiceEnabled && (
        <VoiceTextsFields
          part='callScreen'
          control={control}
          idPrefix={configId || 'webclientModal'}
          inputClassName={inputClassName}
          labelClassName={labelClassName}
        />
      )}
    </div>
  );
};

export default WebclientTextsFields;
